import { FLAG_LABEL, LoadError, Pill, SectionTitle, Skeleton, StatTile, show, useLoad, when } from "./ui";

// What needs the admin today. Counts, states and fixed labels only.

const JOB: Record<string, string> = {
  send_reminder: "A reminder", run_routine: "A repeating text", link_text: "A text between linked people",
  email_watch: "An email check", calendar_watch: "A calendar check",
};

export default function AdminToday({ onOpen }: { onOpen: (tab: string, userId?: string) => void }) {
  const { data, error, loading, reload } = useLoad("/admin/today");
  if (loading && !data) return <Skeleton rows={4} />;
  if (error && !data) return <LoadError message={error} onRetry={reload} />;
  const n = data?.counts || {};
  const worker = data?.worker?.status;
  const needs: any[] = data?.needs_you || [];
  const jobs: any[] | null = data?.failed_jobs ?? null;

  return (
    <div data-testid="admin-today">
      {worker === "down" && (
        <div className="mb-4 rounded-3xl border border-destructive/40 bg-destructive/10 p-5" role="alert"
          data-testid="worker-down">
          <div className="text-sm font-extrabold text-destructive dark:text-red-400">
            Reminders are not going out
          </div>
          <p className="mt-1 text-sm">
            The background worker has not checked in for over two minutes. Reminders, repeating
            texts and email checks wait until it is back. Ask Emergent to check the deployment.
          </p>
        </div>
      )}

      <SectionTitle kicker="Today" title="Needs you"
        note={needs.length ? undefined : "Nothing is waiting on you right now."} />
      {needs.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {needs.map((r, i) => (
            <button key={r.key} type="button"
              onClick={() => (r.kind === "invite" ? onOpen("requests")
                : onOpen("people", String(r.key).replace(/^user:/, "")))}
              className={`block min-h-[56px] w-full px-4 py-3 text-left ${i ? "border-t border-border" : ""}`}
              data-testid={`needs-${r.key}`}>
              <span className="block truncate text-sm font-bold">{r.name}</span>
              <span className="mt-1.5 flex flex-wrap gap-1.5">
                {(r.flags || []).map((f: string) => (
                  <Pill key={f} tone={FLAG_LABEL[f]?.tone}>{FLAG_LABEL[f]?.text || f}</Pill>
                ))}
              </span>
            </button>
          ))}
        </div>
      )}

      <SectionTitle title="So far today"
        note="Days roll over at 7 PM Houston time (midnight UTC), the same as the daily limits." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="People who texted" value={n.active_today} />
        <StatTile label="Texts to Remy" value={n.texts_today} />
        <StatTile label="Replies that failed" value={n.replies_failed_today}
          tone={n.replies_failed_today ? "attention" : undefined} />
        <StatTile label="Asked for, couldn't do" value={n.new_unmet_today} />
        <StatTile label="At their daily limit" value={n.at_limit} tone={n.at_limit ? "watch" : undefined} />
        <StatTile label="Waiting for your OK" value={n.waiting_for_you} />
      </div>

      <SectionTitle title="Everyone" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="People with an account" value={n.people} testid="stat-users" />
        <StatTile label="Invited, not started yet" value={n.invited_not_started} />
        <StatTile label="Gone quiet" value={n.gone_quiet} tone={n.gone_quiet ? "watch" : undefined} />
        <StatTile label="Never started" value={n.never_started} />
        <StatTile label="Paused by you" value={n.paused} tone={n.paused ? "watch" : undefined} />
        <StatTile label="Look unused (likely test accounts)" value={n.looks_unused} />
        <StatTile label="Texted STOP, last 14 days" value={n.texts_off_recent}
          tone={n.texts_off_recent ? "attention" : undefined} />
        <StatTile label="Reminders failed, last 7 days" value={n.reminders_failed_7}
          tone={n.reminders_failed_7 ? "attention" : undefined} />
      </div>

      {!!n.deletions_stuck && (
        <div className="mt-4 rounded-3xl border border-destructive/40 bg-destructive/10 p-5" role="alert">
          <div className="text-sm font-extrabold text-destructive dark:text-red-400">
            {n.deletions_stuck} account deletion{n.deletions_stuck === 1 ? "" : "s"} did not finish
          </div>
          <p className="mt-1 text-sm">Tell your developer: a deletion gave up part-way and needs finishing by hand.</p>
        </div>
      )}

      <SectionTitle title="Background work that gave up"
        note={`Last 7 days: ${show(n.jobs_failed_7)}. The kind and the time only.`} />
      {jobs === null ? (
        <p className="text-sm text-muted-foreground">Couldn't check this just now.</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing gave up this week.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid="stat-jobs">
          {jobs.map((j, i) => (
            <div key={i} className={`flex min-h-[48px] items-center justify-between gap-3 px-4 py-2 text-sm ${i ? "border-t border-border" : ""}`}>
              <span>{JOB[j.type] || String(j.type || "Background job").replace(/_/g, " ")}</span>
              <span className="text-xs text-muted-foreground">{when(j.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
